/**
 * VODSet entity class
 */
import { dateAsUTC } from '../../../app/helpers/date'

/**
 * VODSetFactory
 * returns VODSet entity class
 *
 * @class VODSetFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.VODSet} class
 *
 * @ngdoc factory
 * @name mwApp.factory:VODSetFactory
 */
/* @ngInject */
export default function VODSetFactory ($http, $injector, Entity, FileUploadMixin, onOffMixin, alertLiteSvc) {
  /* === VODSet class === */

  /**
   * VODSet model
   *
   * @class VODSet
   * @classdesc VODSet model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @mixes models.FileUploadMixin
   * @return {models.VODSet} VODSet entity
   */
  function VODSet () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  VODSet.prototype = Object.create(Entity.prototype)
  VODSet.prototype.constructor = VODSet

  /* === VODSet instance methods & fields === */

  Object.assign(VODSet.prototype, {

    /**
     * instance fields
     *
     * @memberof models.VODSet#
     * @type {Object}
     */
    _fields: {
      id: Number,
      networkId: Number,
      network: 'Network',
      name: String,
      quantity: Number,
      enabled: true,
      exclusivePaymentSystemSetting: {
        id: Number,
        assignedPaymentSystems: [{ paymentSystemId: Number, paymentSystemType: String }]
      },
      createdBy: String,
      createdTime: String,
      coverImageUrl: String,
      description: String,
      richDescription: String,
      $createdTime: String,
      updatedBy: String,
      updatedAt: String,
      assignedSubscriptions: ['Subscription'],
      contentItems: [],
      pricing: {
        id: Number,
        rent: {
          amount: Number,
          dueDate: String,
          paymentMethod: String,
          defaultAmount: Number,
          period: Number,
          timeUnit: String
        },
        buy: {
          paymentMethod: String,
          amount: Number,
          defaultAmount: Number
        },
        currency: 'Currency'
      },
      numberOfCcu: Number,
      $overrideCCU: Boolean,
      $buyEnabled: Boolean,
      $rentEnabled: Boolean,
      $assigned: [],
      $available: [],
      $setType: 'vod',
      $itemType: 'vods'
    },

    /**
     * url template
     *
     * @memberof models.VODSet#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/vodsets/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.VODSet#
     * @type {String}
     */
    _publicUrlTemplate: 'organizations/{{::networkId}}/contentSets/vod/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.VODSet#
     * @type {String}
     */
    _subscriptionUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/vodsets',

    /**
     * url template for loading custom prices
     *
     * @memberof models.VODSet#
     * @type {String}
     */
    _customPricesUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/vodsets/{{::setId}}/prices',

    /**
     * url template for checking set intersection
     *
     * @memberof models.VODSet#
     * @type {String}
     */
    _contentIntersectionUrlTemplate: 'api/networks/{{::networkId}}/vodsets/{{::id}}/intersection',

    /**
     * Returns assigned Vods
     *
     * @param {Object} params
     * @memberof models.VODSet#
     * @returns {Promise} promise
     */
    _getVodsAssigned: function (params) {
      if (!this._url) {
        return this._updateUrl().then(() => this._getVodsAssigned(params))
      }

      return $http.get(this._url + '/vods?assigned', { params })
        .data()
        .then((response) => response.payload)
        .catch(rejection => alertLiteSvc.alertError(rejection))
    },

    /**
     * Returns available Vods
     *
     * @param {Object} params
     * @memberof models.VODSet#
     * @returns {Promise} promise
     */
    _getVodsAvailable: function (params) {
      if (!this._url) {
        return this._updateUrl().then(() => this._getVodsAvailable(params))
      }

      return $http.get(this._url + '/vods?available', { params })
        .data()
        .then((response) => response.payload)
        .catch(rejection => alertLiteSvc.alertError(rejection))
    },

    /**
     * Patch method
     *
     * @memberof models.BannerBundle#
     * @param {String} url
     * @param params
     * @return {Promise} promise
     * @private
     */
    _patch: function (url, params) {
      return $http.patch(url, params)
        .then(
          response => {
            if (response.data) {
              this._weakCopy(response.data)
            }
            return response
          }
        ).catch(
          rejection => {
            return alertLiteSvc.alertError(rejection)
          })
    },

    /**
     * On/Off Entity, performing PATCH request to the server
     *
     * @memberof models.VODSet#
     * @param {Boolean} [enabled=this.enabled] - enable or disable Entity
     * @return {Promise} promise
     */
    _onOff: function (enabled) {
      if (typeof enabled !== 'boolean') {
        enabled = this.enabled
      }

      return this._patch(this._url + '/status', enabled ? '"ENABLE"' : '"DISABLE"')
        .catch(rejection => {
          this.enabled = !this.enabled
          return rejection
        })
    },

    /**
     * Prepare object
     *
     * @memberof models.VODSet#
     * @override
     */
    _prepare: function () {
      if (!this.pricing) {
        this.pricing = {
          rent: {
            paymentMethod: 'PER_SET',
            amount: 0,
            period: 1,
            timeUnit: 'DAY'
          },
          buy: {
            paymentMethod: 'PER_SET',
            amount: 0
          }
        }
        this.$buyEnabled = true
        this.$rentEnabled = true
      } else {
        this.$buyEnabled = this.$buyEnabled === null ? !!this.pricing.buy : this.$buyEnabled
        this.$rentEnabled = this.$rentEnabled === null ? !!this.pricing.rent : this.$rentEnabled
        if (!this.$buyEnabled) {
          this.pricing.buy = {
            paymentMethod: 'PER_SET',
            amount: 0
          }
        }
        if (!this.$rentEnabled) {
          this.pricing.rent = {
            paymentMethod: 'PER_SET',
            amount: 0,
            period: 1,
            timeUnit: 'DAY'
          }
        }
      }
      if (this.pricing.rent.dueDate) {
        this.pricing.rent.$periodType = 'DUE_DATE'
        this.pricing.rent.$dueDate = new Date(this.pricing.rent.dueDate)
      } else {
        this.pricing.rent.$periodType = 'DEFAULT'
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.VODSet#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      if (!this.$buyEnabled) {
        this.pricing.buy = null
      }

      if (!this.$rentEnabled) {
        this.pricing.rent = null
      }

      this.contentItems.forEach(ci => {
        if (!this.$buyEnabled) {
          ci.pricing.buy = null
        }
        if (!this.$rentEnabled) {
          ci.pricing.rent = null
        }
      })
      if (this.pricing?.rent) {
        switch (this.pricing.rent.$periodType) {
          case 'DUE_DATE':
            this.pricing.rent.dueDate = dateAsUTC(this.pricing.rent.$dueDate, 'YYYY-MM-DD')
            this.pricing.rent.periodUnlimited = false
            this.pricing.rent.period = 0
            this.pricing.rent.timeUnit = null
            this.pricing.rent.autoPay = false
            break
          case 'DEFAULT':
            this.pricing.rent.dueDate = null
            break
          default:
            break
        }
      }
      return Entity.prototype._object.apply(this, arguments)
    }

  }, FileUploadMixin)

  /* === VODSet static methods & fields === */

  /**
   * Generate url
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {String} [url]
   * @return {String}
   */
  VODSet.generateUrl = function (url = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    return `${cast.prototype.$_urlTemplate(o)}` + url
  }

  /**
   * Returns avaolable Vods
   *
   * @returns {Promise} promise
   */
  VODSet.getVods = function (id, params) {
    return $http.get(`api/networks/${id}/vodsets/vods`, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Returns assigned VODs
   *
   * @returns {Promise} promise
   */
  VODSet.getAssignedVODs = function (id, params) {
    return $http.get(`api/networks/${id}/vodsets/vods?assigned`, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Load entities for SmartTable <br>
   * SmartTable state looks like this:
   *
   * @memberof models.VODSet
   * @param {Object} tableState - SmartTable state
   * @param {Object} [o]        - object over which Url will be interpolated
   * @param {Object} [p]
   * @return {Promise} promise
   */
  VODSet.loadSmartCustom = function (tableState, o, p = {}) {
    const params = { ...p }

    Object.assign(params, tableState.search.predicateObject)

    if (params.$) {
      params.name = params.$
      if (tableState.sort.fieldName) {
        params[tableState.sort.fieldName] = params.$
        delete params.name
      }
      delete params.$
    }

    if (params.selectionType) {
      params['selection-types'] = params.selectionType
      delete params.selectionType
    }

    return VODSet.loadSmart({
      ...tableState,
      search: { predicateObject: params }
    }, o)
  }

  // copy all static methods from Entity class
  Object.assign(VODSet, Entity)
  VODSet._name = 'VODSet'
  VODSet._publicName = 'VOD (Movie) Set'
  VODSet.init()

  // permissions
  VODSet.access = {
    view: '',
    create: '',
    edit: '',
    delete: ''
  }

  return VODSet
}
